import React, { useMemo, useState } from "react";
import {
  Alert,
  Button,
  Card,
  Col,
  Descriptions,
  Flex,
  Form,
  Row,
  Select,
  Statistic,
  Table,
  Tag,
  Typography,
} from "antd";
import { ExperimentOutlined } from "@ant-design/icons";
import { useApiUrl, useCustom, useTranslate } from "@refinedev/core";
import { useSelect } from "@refinedev/antd";
import { usePageStyles } from "../../../shared/styles";
import { CalculatorOptions } from "../../../shared/constants/calculatorOptions";
import {
  ProductRuleField,
  type IProductAvailability,
  type IProductRuleDraft,
  type IProductRuleMetadata,
  type IProductRuleTestContext,
  type IProductRuleTestResult,
} from "../../../models/product-rule";

interface RuleTesterPanelProps {
  /** Unsaved rule to evaluate alongside the saved ones. Nothing is persisted. */
  draft?: IProductRuleDraft;
  /** Prefills the context form, e.g. from the card being tested. */
  initialContext?: IProductRuleTestContext;
  metadata?: IProductRuleMetadata;
}

/**
 * Answers "what would my customer see, and why?".
 *
 * Unlike the product endpoints, which drop blocked products, this returns the
 * full catalogue annotated with the reason — a product that has silently
 * disappeared is exactly what you cannot debug.
 */
export const RuleTesterPanel: React.FC<RuleTesterPanelProps> = ({
  draft,
  initialContext,
  metadata,
}) => {
  const _t = useTranslate();
  const apiUrl = useApiUrl();
  const pageStyles = usePageStyles();
  // Untyped because the `tags` selects yield string[] where the context wants a
  // single string; handleFinish narrows them.
  const [form] = Form.useForm<Record<string, unknown>>();
  const [context, setContext] = useState<IProductRuleTestContext | null>(
    initialContext ?? null,
  );

  const { selectProps: accountSelectProps } = useSelect({
    resource: "accounts",
    optionLabel: "name",
    optionValue: "id",
    searchField: "name",
  });

  const suggestionsFor = (field: ProductRuleField): string[] =>
    metadata?.fields.find((entry) => entry.key === field)?.suggestions ?? [];

  const { data, isFetching, isError, error } = useCustom<IProductRuleTestResult>({
    url: `${apiUrl}/product-rules/test`,
    method: "post",
    config: { payload: { context: context ?? {}, ...(draft ? { draft } : {}) } },
    queryOptions: {
      enabled: context !== null,
      queryKey: ["product-rules-test", context, draft],
    },
  });

  const result = data?.data;

  const blockedFirst = useMemo<IProductAvailability[]>(() => {
    const products = result?.products ?? [];
    return [...products].sort((a, b) => Number(a.available) - Number(b.available));
  }, [result]);

  const renderTextSelect = (field: ProductRuleField, placeholder: string) => {
    const options =
      field === ProductRuleField.ecu
        ? CalculatorOptions.map((value) => ({ label: value, value }))
        : suggestionsFor(field).map((value) => ({ label: value, value }));
    return (
      <Select
        allowClear
        showSearch
        placeholder={placeholder}
        options={options}
        // Free text: the customer's form accepts anything for these fields, so
        // the suggestions must stay typeable. handleFinish takes the first value.
        mode={field === ProductRuleField.ecu ? undefined : "tags"}
        className="w-full"
      />
    );
  };

  const handleFinish = (values: Record<string, unknown>) => {
    // The `tags` selects yield arrays; the API wants a single value per field.
    const single = (value: unknown): string | undefined => {
      if (Array.isArray(value)) return value[0] as string | undefined;
      return (value as string) || undefined;
    };
    setContext({
      accountId: values.accountId as number | undefined,
      make: single(values.make),
      model: single(values.model),
      gearbox: single(values.gearbox),
      engine: single(values.engine),
      ecu: single(values.ecu),
    });
  };

  return (
    <Flex vertical gap={16}>
      <Card style={pageStyles.stepperCard}>
        <Form
          form={form}
          layout="vertical"
          initialValues={initialContext}
          onFinish={handleFinish}
        >
          <Row gutter={[12, 0]}>
            <Col xs={24} sm={8}>
              <Form.Item
                name="accountId"
                label={_t("product-rules.tester.account", "Account")}
              >
                <Select {...accountSelectProps} allowClear />
              </Form.Item>
            </Col>
            <Col xs={24} sm={8}>
              <Form.Item name="make" label={_t("product-rules.fields.fields.make", "Make")}>
                {renderTextSelect(ProductRuleField.make, "Audi")}
              </Form.Item>
            </Col>
            <Col xs={24} sm={8}>
              <Form.Item name="model" label={_t("product-rules.fields.fields.model", "Model")}>
                {renderTextSelect(ProductRuleField.model, "A3")}
              </Form.Item>
            </Col>
            <Col xs={24} sm={8}>
              <Form.Item name="gearbox" label={_t("product-rules.fields.fields.gearbox", "Gearbox")}>
                {renderTextSelect(ProductRuleField.gearbox, "DSG")}
              </Form.Item>
            </Col>
            <Col xs={24} sm={8}>
              <Form.Item name="engine" label={_t("product-rules.fields.fields.engine", "Engine")}>
                {renderTextSelect(ProductRuleField.engine, "2.0 TDI")}
              </Form.Item>
            </Col>
            <Col xs={24} sm={8}>
              <Form.Item name="ecu" label={_t("product-rules.fields.fields.ecu", "ECU")}>
                {renderTextSelect(ProductRuleField.ecu, "Bosch")}
              </Form.Item>
            </Col>
          </Row>
          <Button
            type="primary"
            icon={<ExperimentOutlined />}
            htmlType="submit"
            loading={isFetching}
          >
            {_t("product-rules.tester.run", "Test")}
          </Button>
        </Form>
      </Card>

      {isError && (
        <Alert
          type="error"
          showIcon
          message={_t("product-rules.tester.error", "Could not run the test")}
          description={error?.message}
        />
      )}

      {result && (
        <>
          {/* A rule that "does not fire" is nearly always a context problem —
              a blank field or an account with no group — so what the engine
              actually matched on goes first, not hidden behind the results. */}
          <Card style={pageStyles.stepperCard}>
            <Typography.Text strong className="mb-3 block uppercase text-sm">
              {_t("product-rules.tester.resolvedContext", "Evaluated context")}
            </Typography.Text>
            <Descriptions size="small" column={{ xs: 1, sm: 2, md: 3 }} bordered>
              <Descriptions.Item label={_t("product-rules.fields.fields.make", "Make")}>
                {result.resolvedContext.make ?? "—"}
              </Descriptions.Item>
              <Descriptions.Item label={_t("product-rules.fields.fields.model", "Model")}>
                {result.resolvedContext.model ?? "—"}
              </Descriptions.Item>
              <Descriptions.Item label={_t("product-rules.fields.fields.gearbox", "Gearbox")}>
                {result.resolvedContext.gearbox ?? "—"}
              </Descriptions.Item>
              <Descriptions.Item label={_t("product-rules.fields.fields.engine", "Engine")}>
                {result.resolvedContext.engine ?? "—"}
              </Descriptions.Item>
              <Descriptions.Item label={_t("product-rules.fields.fields.ecu", "ECU")}>
                {result.resolvedContext.ecu ?? "—"}
              </Descriptions.Item>
              <Descriptions.Item
                label={_t("product-rules.fields.fields.accountGroup", "Account group")}
              >
                {result.resolvedContext.accountGroup?.name ?? "—"}
              </Descriptions.Item>
            </Descriptions>
          </Card>

          <Row gutter={16}>
            <Col xs={8}>
              <Card style={pageStyles.stepperCard}>
                <Statistic
                  title={_t("product-rules.tester.total", "Products")}
                  value={result.summary.total}
                />
              </Card>
            </Col>
            <Col xs={8}>
              <Card style={pageStyles.stepperCard}>
                <Statistic
                  title={_t("product-rules.tester.available", "Available")}
                  value={result.summary.available}
                />
              </Card>
            </Col>
            <Col xs={8}>
              <Card style={pageStyles.stepperCard}>
                <Statistic
                  title={_t("product-rules.tester.blocked", "Blocked")}
                  value={result.summary.blocked}
                />
              </Card>
            </Col>
          </Row>

          <Table
            dataSource={blockedFirst}
            rowKey="id"
            size="small"
            pagination={false}
            expandable={{
              rowExpandable: (record) => !!record.blockedBy,
              expandedRowRender: (record) => (
                <Descriptions size="small" column={1} bordered>
                  {(record.blockedBy?.matchedConditions ?? []).map((condition) => (
                    <Descriptions.Item
                      key={condition.field}
                      label={`${condition.field} ${condition.operator} [${condition.values.join(", ")}]`}
                    >
                      <Flex gap={8} align="center">
                        <Tag color={condition.matched ? "red" : "default"}>
                          {condition.matched
                            ? _t("product-rules.tester.matched", "matched")
                            : _t("product-rules.tester.notMatched", "not matched")}
                        </Tag>
                        <Typography.Text type="secondary">
                          {_t("product-rules.tester.contextValue", "value")}:{" "}
                          {condition.contextValue ?? "—"}
                        </Typography.Text>
                      </Flex>
                    </Descriptions.Item>
                  ))}
                </Descriptions>
              ),
            }}
          >
            <Table.Column
              dataIndex="name"
              title={_t("products.fields.name", "Product")}
              render={(value: string, record: IProductAvailability) => (
                <Flex vertical>
                  <Typography.Text strong>{value}</Typography.Text>
                  <Typography.Text type="secondary" className="text-xs">
                    {record.code}
                  </Typography.Text>
                </Flex>
              )}
            />
            <Table.Column
              dataIndex="available"
              title={_t("product-rules.tester.availability", "Availability")}
              width={140}
              render={(available: boolean) => (
                <Tag color={available ? "green" : "red"}>
                  {available
                    ? _t("product-rules.tester.availableTag", "Available")
                    : _t("product-rules.tester.blockedTag", "Blocked")}
                </Tag>
              )}
            />
            <Table.Column
              title={_t("product-rules.tester.blockedBy", "Blocked by")}
              render={(_value, record: IProductAvailability) =>
                record.blockedBy ? (
                  <Flex vertical>
                    <Flex gap={6} align="center">
                      <Typography.Text strong>
                        {record.blockedBy.ruleName}
                      </Typography.Text>
                      {record.blockedBy.isDraft && (
                        <Tag color="blue">
                          {_t("product-rules.tester.draftTag", "draft")}
                        </Tag>
                      )}
                    </Flex>
                    {record.blockedBy.message && (
                      <Typography.Text type="secondary" className="text-xs">
                        {record.blockedBy.message}
                      </Typography.Text>
                    )}
                  </Flex>
                ) : (
                  "—"
                )
              }
            />
          </Table>
        </>
      )}
    </Flex>
  );
};
